import { useCallback, useEffect, useRef } from "react";
import { useLatestRef } from "@design-system/hooks/useLatestRef";

/**
 * Returns a stable function that runs `callback` on the next animation frame.
 * A newer request replaces a pending one, and unmounting cancels it.
 */
export function useNextFrame(callback: () => void) {
    const callbackRef = useLatestRef(callback);
    const frameRef = useRef<number | null>(null);

    const cancel = useCallback(() => {
        if (frameRef.current !== null) {
            cancelAnimationFrame(frameRef.current);
            frameRef.current = null;
        }
    }, []);

    useEffect(() => cancel, [cancel]);

    return useCallback(() => {
        cancel();
        frameRef.current = requestAnimationFrame(() => {
            frameRef.current = null;
            callbackRef.current();
        });
    }, [cancel, callbackRef]);
}
